'use client'

import PowerLogo from '@components/app/PowerLogo'
import PowerLogoIcon from '@components/app/PowerLogo/PowerLogoIcon'
import { Icon } from '@components/primitives/Icon'
import Link from '@components/primitives/Link'
import { classMerge } from '@components/utilities/classMerge'
import { faSlidersH } from '@fortawesome/pro-regular-svg-icons/faSlidersH'
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch'
import { colors } from '@lib/colors/colors'
import Routes from '@lib/routes'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import deleteFacetFilterFromSlug from '@modules/trials/search/facetFilters/deleteFacetFilterFromSlug'
import type { SearchLanderPageData } from '@modules/trials/search/getSearchLanderPageDataFromProps'
import { usePathname } from 'next/navigation'
import { use } from 'react'
import { useConditionFacetRefinement } from '../Hooks/useConditionFacetRefinement'
import { useHandleFacetClick } from '../Hooks/useHandleFacetClick.client'
import { useLocationFacetRefinement } from '../Hooks/useLocationFacetRefinement'
import { useToggleFilterModal } from '../Hooks/useToggleFilterModal'
import { SearchSuggestionInput } from './SearchSuggestionInput'

export default function SearchBar({
  searchLanderPageData,
}: {
  searchLanderPageData: Promise<SearchLanderPageData>
}) {
  const { activeConditionLabel, activeLocationLabel } =
    use(searchLanderPageData)
  const pathname = usePathname()

  const toggleFilterModal = useToggleFilterModal()
  const handleFacetClick = useHandleFacetClick()

  const {
    searchTerm,
    selectedCondition,
    setSearchTerm,
    setSelectedCondition,
    suggestions,
  } = useConditionFacetRefinement({
    initialCondition: activeConditionLabel ?? '',
  })

  const {
    handlePredictionClick,
    navigateWithFilters,
    predictions: locationPredictions,
    searchTerm: locationSearchTerm,
    setSearchTerm: setLocationSearchTerm,
  } = useLocationFacetRefinement({
    initialLocation: activeLocationLabel,
  })

  return (
    <>
      {/* Desktop search bar */}
      <div className='hidden w-full items-center lg:grid lg:grid-cols-[auto,1fr,320px] lg:gap-3 lg:px-0 lg:pl-6'>
        <div className='ml-4 flex items-center justify-start pt-4 sm:pt-0 lg:ml-0 lg:w-[320px]'>
          <Link href={Routes.home}>
            <PowerLogo
              className='flex items-center justify-start'
              iconColor={colors.black}
              nameOpacity={1}
              size='Large'
            />
          </Link>
        </div>
        <div className='flex justify-center lg:flex-initial lg:pb-6 lg:pt-4'>
          <div className='relative w-full lg:mx-0 lg:h-[62px] lg:w-[566px] lg:max-w-[566px]'>
            <div className='group flex size-full items-center justify-between overflow-hidden rounded-lg border-2 border-neutral200 bg-white'>
              <div className='flex w-full items-center'>
                {/* Condition selector */}
                <SearchSuggestionInput<string>
                  label='Condition'
                  onClearClick={(item) => {
                    handleFacetClick({
                      facetType: 'condition',
                      selectedValue: selectedCondition,
                      setSelectedValue: setSelectedCondition,
                      value: item,
                    })
                  }}
                  onInputChange={(e) => setSearchTerm(e.target.value)}
                  onSuggestionClick={(item) => {
                    handleFacetClick({
                      facetType: 'condition',
                      selectedValue: selectedCondition,
                      setSelectedValue: setSelectedCondition,
                      value: item,
                    })
                    trackEvent(
                      TrackingEvents.SEARCH_LANDER_CONDITION_FILTER_CLICKED,
                      {
                        val: 'from-search-bar',
                      },
                    )
                  }}
                  placeholder='Type Condition'
                  renderSuggestion={(item) => item}
                  suggestions={suggestions}
                  value={searchTerm}
                />
                <div className='mx-2 h-[30px] w-0.5 rounded bg-neutral100 transition-opacity duration-200 group-focus-within:opacity-0 group-hover:opacity-0' />
                {/* Location selector */}
                <SearchSuggestionInput<google.maps.places.AutocompletePrediction>
                  label='Location'
                  onClearClick={() => {
                    setLocationSearchTerm('')
                    const url = deleteFacetFilterFromSlug({
                      facet: 'city',
                      slug: pathname,
                    })
                    navigateWithFilters(url)
                  }}
                  onInputChange={(e) => setLocationSearchTerm(e.target.value)}
                  onSuggestionClick={(item) => {
                    handlePredictionClick(item)
                    trackEvent(
                      TrackingEvents.SEARCH_LANDER_LOCATION_FILTER_CLICKED,
                      {
                        val: 'from-search-bar',
                      },
                    )
                  }}
                  placeholder='Current Location'
                  renderSuggestion={(item) => item.description}
                  suggestions={locationPredictions}
                  value={locationSearchTerm}
                />
              </div>
              {/* Search button - desktop */}
              <div className='hidden border-l-2 border-neutral200 hover:bg-neutral100 lg:flex'>
                <button className='flex size-[42px] items-center justify-center px-2'>
                  <Icon
                    className='stroke-black text-neutral700'
                    icon={faSearch}
                    size='lg'
                  />
                </button>
              </div>
              {/* Filter button - mobile */}
            </div>
          </div>
        </div>
      </div>
      {/* Mobile search bar */}
      <div className='mt-4 flex w-full'>
        <div className='mx-4 flex w-full items-center justify-center lg:hidden'>
          <Link className='mr-3 mt-3' href={Routes.home}>
            <PowerLogoIcon
              fillColor={colors.black}
              opacity={1}
              overrideSize={32}
            />
          </Link>
          <div
            className='relative flex w-full items-center justify-between rounded-lg border-2 border-neutral200 bg-white px-4'
            onClick={() => {
              toggleFilterModal()
              trackEvent(TrackingEvents.SEARCH_LANDER_FILTERS_OPENED, {
                val: 'from-search-bar',
              })
            }}
          >
            <div className='block py-1.5'>
              <p
                className={classMerge(
                  'text-sm font-bold tracking-wide',
                  selectedCondition ? 'text-black' : 'text-neutral500',
                )}
              >
                {selectedCondition ? selectedCondition : 'Type Condition'}
              </p>
              <p
                className={classMerge(
                  'text-[length:0.6875rem] font-medium tracking-wide text-neutral900',
                  activeLocationLabel ? 'text-neutral900' : 'text-neutral500',
                )}
              >
                {activeLocationLabel ? activeLocationLabel : 'Current Location'}
              </p>
            </div>
            <div className='flex items-center justify-center rounded-full bg-neutral100 px-3 py-1.5 transition-all active:bg-neutral300 lg:hidden'>
              <button>
                <Icon icon={faSlidersH} size='1x' />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
